/**
 * Types + fetchery dla pluginu `c_photo_gallery` rozszerzającego shell
 * `c_user_accounts` (zarządzanie własnymi galeriami przez zalogowanego usera).
 *
 * Backend (IRESTAPI, routing po NAZWIE METODY - `IAPI_` strip):
 *   - GET    /IRESTAPI/v1/C_PHOTO_GALLERY_CONTROLLER/userGalleries?page=&per_page=
 *   - GET    /IRESTAPI/v1/C_PHOTO_GALLERY_CONTROLLER/userGallery/{id}
 *   - POST   /IRESTAPI/v1/C_PHOTO_GALLERY_CONTROLLER/userGallerySave
 *   - PUT    /IRESTAPI/v1/C_PHOTO_GALLERY_CONTROLLER/userGalleryUpdate/{id}
 *   - DELETE /IRESTAPI/v1/C_PHOTO_GALLERY_CONTROLLER/userGalleryDelete/{id}
 *   - GET    /IRESTAPI/v1/C_PHOTO_GALLERY_CONTROLLER/userGalleryImages/{id}
 *   - POST   /IRESTAPI/v1/C_PHOTO_GALLERY_CONTROLLER/userGalleryImageUpload/{id} (multipart)
 *   - DELETE /IRESTAPI/v1/C_PHOTO_GALLERY_CONTROLLER/userGalleryImageDelete/{id}/{image_id}
 *   - POST   /IRESTAPI/v1/C_PHOTO_GALLERY_CONTROLLER/userGalleryImageSetMain/{id}/{image_id}
 *
 * Frontend route handlery (proxy + Bearer) w app/api/iiapi/c-photo-gallery/.
 */

export type GalleryCover = {
  name: string;
  thumb_src: string;
  width: number | null;
  height: number | null;
};

export type UserGallery = {
  id: number;
  name: string;
  text: string;
  /** 0 = niepubliczna, 1 = publiczna. */
  public: number;
  count_pictures: number;
  cover: GalleryCover | null;
  /** Publiczny URL galerii na stronie (ILINK) - przycisk "Zobacz na stronie".
      Pusty string gdy katalog nie ma symbolu. */
  public_href: string;
};

export type UserGalleryImage = {
  id: number;
  name: string;
  description: string;
  is_main: boolean;
  thumb_src: string;
  full_src: string;
  width: number | null;
  height: number | null;
};

export type GalleryPagination = {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
};

/** Wspólny kształt odpowiedzi route handlera (sukces/błąd). */
type Envelope<T> = ({ ok: true } & T) | { ok: false; error?: string; message?: string; code?: number | string };

async function readJson<T>(res: Response, fallbackMsg: string): Promise<T> {
  const body = (await res.json()) as Envelope<{ data: T }>;
  if (!res.ok || !("ok" in body) || body.ok !== true) {
    const msg = "message" in body && body.message ? body.message : `${fallbackMsg} (HTTP ${res.status})`;
    throw new Error(msg);
  }
  return body.data;
}

/** Lista galerii usera (paginowana). */
export async function fetchUserGalleries(
  page = 1,
  perPage = 20,
): Promise<{ galleries: UserGallery[]; pagination: GalleryPagination }> {
  const res = await fetch(`/api/iiapi/c-photo-gallery/galleries?page=${page}&per_page=${perPage}`, {
    cache: "no-store",
  });
  return readJson(res, "Nie udało się pobrać galerii");
}

/** Pojedyncza galeria (meta) - do formularza edycji. */
export async function fetchUserGallery(id: number): Promise<{ gallery: UserGallery }> {
  const res = await fetch(`/api/iiapi/c-photo-gallery/gallery/${id}`, { cache: "no-store" });
  return readJson(res, "Nie udało się pobrać galerii");
}

export type GalleryFormPayload = { name: string; text: string; public: boolean };

/** Utworzenie galerii. Zwraca id nowej galerii. */
export async function createUserGallery(payload: GalleryFormPayload): Promise<{ gallery_id: number }> {
  const res = await fetch(`/api/iiapi/c-photo-gallery/galleries`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    cache: "no-store",
  });
  return readJson(res, "Nie udało się utworzyć galerii");
}

/** Aktualizacja galerii. */
export async function updateUserGallery(id: number, payload: GalleryFormPayload): Promise<{ success: boolean }> {
  const res = await fetch(`/api/iiapi/c-photo-gallery/gallery/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    cache: "no-store",
  });
  return readJson(res, "Nie udało się zapisać galerii");
}

/** Usuniecie galerii (wraz ze zdjęciami i plikami). */
export async function deleteUserGallery(id: number): Promise<{ success: boolean }> {
  const res = await fetch(`/api/iiapi/c-photo-gallery/gallery/${id}`, {
    method: "DELETE",
    cache: "no-store",
  });
  return readJson(res, "Nie udało się usunąć galerii");
}

/** Lista zdjęć w galerii. */
export async function fetchGalleryImages(id: number): Promise<{ images: UserGalleryImage[] }> {
  const res = await fetch(`/api/iiapi/c-photo-gallery/gallery/${id}/images`, { cache: "no-store" });
  return readJson(res, "Nie udało się pobrać zdjęć");
}

/** Upload zdjęć (multipart). `files` z input[type=file] multiple. */
export async function uploadGalleryImages(
  id: number,
  files: FileList | File[],
): Promise<{ uploaded: number; errors: string[] }> {
  const fd = new FormData();
  for (const f of Array.from(files)) fd.append("images[]", f);

  const res = await fetch(`/api/iiapi/c-photo-gallery/gallery/${id}/images`, {
    method: "POST",
    body: fd,
    cache: "no-store",
  });
  return readJson(res, "Nie udało się wgrać zdjęć");
}

/** Usuniecie pojedynczego zdjęcia. */
export async function deleteGalleryImage(id: number, imageId: number): Promise<{ success: boolean }> {
  const res = await fetch(`/api/iiapi/c-photo-gallery/gallery/${id}/image/${imageId}`, {
    method: "DELETE",
    cache: "no-store",
  });
  return readJson(res, "Nie udało się usunąć zdjęcia");
}

/** Ustawienie zdjęcia głównego (okładki). */
export async function setGalleryMainImage(id: number, imageId: number): Promise<{ success: boolean }> {
  const res = await fetch(`/api/iiapi/c-photo-gallery/gallery/${id}/image/${imageId}/main`, {
    method: "POST",
    cache: "no-store",
  });
  return readJson(res, "Nie udało się ustawić zdjęcia głównego");
}
